import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import useExperienceThemes, { type ExperienceTheme } from '../hooks/useExperienceThemes'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Expander from 'components/utils/Expander'
import Clickable from 'components/Common/Clickable'
import { unique, without } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const ListContainer = styled.div`
  margin-top: ${rem(6)};
`

const ThemeItem = styled.div.attrs<{ $isCategory?: boolean }>((props) => ({
  className: props.$isCategory ? 'category-theme' : undefined,
}))`
  & + & {
    margin-top: ${rem(8)};
  }

  &.category-theme + &.category-theme {
    margin-top: ${rem(4)};
  }
`

const ThemeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${rem(24)};
  align-items: center;
  column-gap: ${rem(4)};
`

const ExpandButton = styled(Clickable)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${rem(24)};
  height: ${rem(24)};
`

const ChildList = styled.div`
  margin-left: ${rem(36)};
  padding-block: ${rem(8)};
`

const CountText = styled(BodyText)`
  margin-left: ${rem(4)};
`

interface Props {
  place?: App.Place;
}

function isCategoryTheme(theme: ExperienceTheme): theme is Extract<ExperienceTheme, { type: 'category' }> {
  return theme.type === 'category'
}

function ThemeCount({ count }: { count: number }) {
  const intl = useIntl()

  return (
    <CountText as="span" variant="medium" colour="neutral-two">
      {intl.formatMessage(
        {
          id: 'EfkvoARy0J9lyukUXTWh',
          defaultMessage: '({count})',
          description:
            'Count of matching experiences shown next to a theme filter option',
        },
        { count },
      )}
    </CountText>
  )
}

interface ThemeCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  children?: React.ReactNode;
}

function ThemeCheckbox({
  checked,
  disabled,
  onChange,
  children,
}: ThemeCheckboxProps) {
  return (
    <CheckboxInput
      size="medium"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    >
      <BodyText as="span" variant="medium">
        {children}
      </BodyText>
    </CheckboxInput>
  )
}

function ExperienceSearchThemesFilter({ place }: Props) {
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const themes = useExperienceThemes(place)
  const trackEvent = useTrackExperienceOfferEvent()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const [expandedThemeIds, setExpandedThemeIds] = useState<Set<string>>(() => new Set())

  const selectedCategoryCodes = useMemo(
    () => searchFilters.categoryCodes ?? [],
    [searchFilters.categoryCodes],
  )

  const selectedCollections = useMemo(
    () => searchFilters.collections ?? [],
    [searchFilters.collections],
  )

  const getCategoryThemeChildIds = useCallback(
    (theme: Extract<ExperienceTheme, { type: 'category' }>) =>
      theme.children.length > 0
        ? theme.children
            .filter((child) => child.count > 0 || selectedCategoryCodes.includes(child.id))
            .map((child) => child.id)
        : [theme.categoryCode],
    [selectedCategoryCodes],
  )

  const getCategoryThemeIds = useCallback(
    (theme: Extract<ExperienceTheme, { type: 'category' }>) =>
      unique([theme.categoryCode, ...getCategoryThemeChildIds(theme)]),
    [getCategoryThemeChildIds],
  )

  const isCategoryThemeSelected = useCallback(
    (theme: Extract<ExperienceTheme, { type: 'category' }>) => {
      const childIds = getCategoryThemeChildIds(theme)
      return selectedCategoryCodes.includes(theme.categoryCode) ||
        (childIds.length > 0 && childIds.every((categoryCode) => selectedCategoryCodes.includes(categoryCode)))
    },
    [getCategoryThemeChildIds, selectedCategoryCodes],
  )

  const isCollectionThemeSelected = useCallback(
    (theme: Extract<ExperienceTheme, { type: 'collection' }>) => {
      return selectedCollections.includes(theme.collectionId)
    },
    [selectedCollections],
  )

  useEffect(() => {
    if (themes.length > 0) {
      trackEvent('THEME_FILTER_IMPRESSION')
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [themes.length])

  const onToggleCategoryTheme = useCallback(
    (theme: Extract<ExperienceTheme, { type: 'category' }>) => {
      const childIds = getCategoryThemeChildIds(theme)
      const themeCategoryIds = getCategoryThemeIds(theme)
      const selected = isCategoryThemeSelected(theme)
      const next = selected
        ? without(selectedCategoryCodes, ...themeCategoryIds)
        : unique([...without(selectedCategoryCodes, theme.categoryCode), ...childIds])

      trackSearchFilter({
        label: theme.title,
        type: 'theme_category',
        value: selected ? 'unselected' : 'selected',
      })
      setSearchFilters({
        ...searchFilters,
        categoryCodes: next.length > 0 ? next : undefined,
      })
      trackEvent('CATEGORY_THEME_FILTER_SELECTED', undefined, `category theme filter: ${theme.title}`)
    },
    [getCategoryThemeChildIds, getCategoryThemeIds, isCategoryThemeSelected, selectedCategoryCodes, searchFilters, setSearchFilters, trackEvent, trackSearchFilter],
  )

  const onToggleCategoryChild = useCallback(
    (theme: Extract<ExperienceTheme, { type: 'category' }>, categoryCode: number) => {
      const themeSelectedByParent = selectedCategoryCodes.includes(theme.categoryCode)
      const childSelected = themeSelectedByParent || selectedCategoryCodes.includes(categoryCode)
      const currentCategoryCodes = themeSelectedByParent
        ? unique([...without(selectedCategoryCodes, theme.categoryCode), ...getCategoryThemeChildIds(theme)])
        : selectedCategoryCodes
      const next = childSelected
        ? without(currentCategoryCodes, categoryCode)
        : unique([...without(currentCategoryCodes, theme.categoryCode), categoryCode])
      const child = theme.children.find(child => child.id === categoryCode)

      trackSearchFilter({
        label: child?.name ?? String(categoryCode),
        type: 'theme_subcategory',
        value: childSelected ? 'unselected' : 'selected',
      })
      setSearchFilters({
        ...searchFilters,
        categoryCodes: next.length > 0 ? next : undefined,
      })
      trackEvent('CATEGORY_THEME_FILTER_SELECTED', undefined, `category theme filter: ${theme.title}: ${categoryCode}`)
    },
    [getCategoryThemeChildIds, selectedCategoryCodes, searchFilters, setSearchFilters, trackEvent, trackSearchFilter],
  )

  const onToggleCollection = useCallback(
    (theme: Extract<ExperienceTheme, { type: 'collection' }>) => {
      const selected = selectedCollections.includes(theme.collectionId)
      const next = selected
        ? selectedCollections.filter((id) => id !== theme.collectionId)
        : [...selectedCollections, theme.collectionId]
      trackSearchFilter({
        label: theme.title,
        type: 'theme_collection',
        value: selected ? 'unselected' : 'selected',
      })
      setSearchFilters({
        ...searchFilters,
        collections: next.length > 0 ? next : undefined,
      })
      trackEvent('COLLECTION_THEME_FILTER_SELECTED', undefined, `collection theme filter: ${theme.title}`)
    },
    [selectedCollections, searchFilters, setSearchFilters, trackEvent, trackSearchFilter],
  )

  const onToggleExpanded = useCallback(
    (themeId: string) => {
      setExpandedThemeIds((current) => {
        const next = new Set(current)
        if (next.has(themeId)) {
          next.delete(themeId)
        } else {
          next.add(themeId)
        }
        return next
      })
    },
    [],
  )

  if (themes.length === 0) {
    return null
  }

  return (
    <ListContainer>
      {themes.map((theme) => {
        if (!isCategoryTheme(theme)) {
          return (
            <ThemeItem key={theme.id}>
              <ThemeCheckbox
                checked={isCollectionThemeSelected(theme)}
                onChange={() => onToggleCollection(theme)}
                disabled={theme.count === 0}
              >
                {theme.title}
              </ThemeCheckbox>
            </ThemeItem>
          )
        }

        const expanded = expandedThemeIds.has(theme.id)
        const visibleChildren = theme.children.filter(
          (child) => child.count > 0 || selectedCategoryCodes.includes(child.id),
        )

        return (
          <ThemeItem key={theme.id} $isCategory>
            <ThemeRow>
              <ThemeCheckbox
                checked={isCategoryThemeSelected(theme)}
                onChange={() => onToggleCategoryTheme(theme)}
                disabled={theme.count === 0}
              >
                {theme.title}
              </ThemeCheckbox>
              {visibleChildren.length > 0 && (
                <ExpandButton
                  onClick={() => onToggleExpanded(theme.id)}
                  aria-expanded={expanded}
                  aria-label={theme.title}
                >
                  <DropdownChevron open={expanded} />
                </ExpandButton>
              )}
            </ThemeRow>
            <Expander expanded={expanded}>
              <ChildList>
                <VerticalSpacer gap={8}>
                  {visibleChildren.map((child) => (
                    <ThemeCheckbox
                      key={child.id}
                      checked={selectedCategoryCodes.includes(theme.categoryCode) || selectedCategoryCodes.includes(child.id)}
                      onChange={() => onToggleCategoryChild(theme, child.id)}
                    >
                      {child.name}
                      <ThemeCount count={child.count} />
                    </ThemeCheckbox>
                  ))}
                </VerticalSpacer>
              </ChildList>
            </Expander>
          </ThemeItem>
        )
      })}
    </ListContainer>
  )
}

export default ExperienceSearchThemesFilter
